import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { primaryHomeForRoles } from "@/lib/auth";
import Logo from "@/components/Logo";
import DemoLoginButtons from "@/components/DemoLoginButtons";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const { login } = useAuth();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", {
        email, password, totp_code: needsTotp ? totpCode : undefined,
      });
      if (data.requires_2fa_setup) {
        toast.info("Configura tu 2FA para continuar.");
        nav("/setup-2fa", { state: { setup_token: data.setup_token, qr_data_url: data.qr_data_url, email } });
        return;
      }
      if (data.requires_2fa) {
        setNeedsTotp(true);
        toast.info("Ingresa el código TOTP de tu app.");
        return;
      }
      login(data);
      toast.success(`Bienvenido, ${data.user.full_name}`);
      // Honor ?next= query param (used by /carrito and /checkout redirects) with a
      // fallback chain to location.state?.from and finally the role's home.
      const params = new URLSearchParams(location.search);
      const nextParam = params.get("next");
      // Whitelist to avoid open-redirect: internal paths only.
      const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
      const to = safeNext || location.state?.from || primaryHomeForRoles(data.user.roles);
      nav(to);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="p-6"><Link to="/"><Logo /></Link></div>
      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-md">
          <DemoLoginButtons onPrefill={({ email, password }) => {
            setEmail(email); setPassword(password); setNeedsTotp(false);
          }} />
          <div className="p-8 bg-[#F9FAFB] border border-gray-200 rounded-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#F26A21] font-semibold">
              <ShieldCheck className="w-4 h-4" /> Acceso seguro
            </div>
            <h1 className="mt-2 text-3xl font-bold text-[#0B0B0B] font-display">Ingresa a tu cuenta</h1>
            <p className="mt-2 text-sm text-gray-600">Usa las credenciales que te asignó tu institución o crea una cuenta.</p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">Correo</label>
                <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">Contraseña</label>
                <input data-testid="login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
              </div>
              {needsTotp && (
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">Código 2FA (6 dígitos)</label>
                  <input data-testid="login-totp" required maxLength={6} value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full mt-1 bg-white border border-gray-200 rounded-md px-3 py-3 text-center font-mono text-xl tracking-widest text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
                </div>
              )}
              <button type="submit" disabled={submitting} data-testid="login-submit-btn" className="w-full py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-60 transition-all">
                {submitting ? "Ingresando..." : needsTotp ? "Verificar código" : "Ingresar"}
              </button>
            </form>

            <div className="mt-4 flex items-center justify-between text-sm">
              <Link to="/olvide-password" data-testid="login-forgot-link" className="text-[#F26A21] hover:text-[#0B0B0B]">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <p className="mt-6 text-center text-sm text-gray-600">
              ¿No tienes cuenta? <Link to="/registro" className="text-[#F26A21] hover:text-[#0B0B0B]">Crear cuenta</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
